import type { SvgIconComponent } from '@mui/icons-material';
import {
  ArchitectureOutlined,
  BuildOutlined,
  DatasetOutlined,
  FactoryOutlined,
  Inventory2Outlined,
  SensorsOutlined,
  StorefrontOutlined,
  VerifiedOutlined,
  ViewTimelineOutlined,
} from '@mui/icons-material';
import { canAccessRoutePath } from '../utils/getDefaultAccessiblePath';

export interface NavPage {
  label: string;
  path: string;
}

/** Módulo da sidebar: rótulo + páginas. */
export interface NavModule {
  /** Identidade estável (chave de lista e da categoria recolhida). */
  id: string;
  label: string;
  /** Rota usada só para decidir o acesso ao módulo. */
  path: string;
  icon: SvgIconComponent;
  pages: NavPage[];
}

/**
 * Mapa único da navegação (sidebar e busca "Ir para").
 * O acesso continua decidido por rota em `canAccessRoutePath` — mesmas permissões de antes.
 */
export const NAV_MODULES: NavModule[] = [
  {
    id: 'planejamento',
    label: 'Planejamento',
    path: '/production',
    icon: ViewTimelineOutlined,
    pages: [
      { label: 'Ordens', path: '/production/planning/ordens' },
      { label: 'Processos', path: '/production/planning/processos' },
      { label: 'Sequenciamento', path: '/production/schedule' },
      { label: 'Parametrização', path: '/production/resources' },
    ],
  },
  {
    id: 'producao',
    label: 'Produção',
    path: '/production',
    icon: FactoryOutlined,
    pages: [
      { label: 'Apontamento', path: '/production/apontamento' },
      { label: 'Monitoramento', path: '/production/monitoring' },
      { label: 'Fábrica', path: '/production/factory-view' },
      { label: 'Auditoria', path: '/production/control/auditoria' },
      { label: 'Não conformidades', path: '/production/control/nao-conformidades' },
      { label: 'Lotes', path: '/production/control/lotes' },
      { label: 'Dashboards', path: '/production/dashboards' },
      { label: 'Relatórios', path: '/production/control/relatorios' },
      { label: 'Melhoria', path: '/production/melhoria' },
    ],
  },
  {
    id: 'qualidade',
    label: 'Qualidade',
    path: '/quality',
    icon: VerifiedOutlined,
    pages: [
      { label: 'Operação', path: '/quality/operacao' },
      { label: 'Checklist', path: '/quality/checklist' },
      { label: 'Auditoria', path: '/quality/auditoria' },
      { label: 'Dashboards', path: '/quality/dashboards' },
      { label: 'Planejamento', path: '/quality/planejamento' },
      { label: 'Parâmetros', path: '/quality/parametros' },
      { label: 'Regras', path: '/quality/regras' },
    ],
  },
  {
    id: 'manutencao',
    label: 'Manutenção',
    path: '/maintenance',
    icon: BuildOutlined,
    pages: [
      { label: 'Painel', path: '/maintenance/dashboards' },
      { label: 'Controle', path: '/maintenance/control' },
      { label: 'Planejamento', path: '/maintenance/planning' },
      { label: 'Sensores', path: '/maintenance/parametrizacao/sensores' },
    ],
  },
  {
    id: 'engenharia',
    label: 'Engenharia',
    path: '/product-engineering',
    icon: ArchitectureOutlined,
    pages: [
      { label: 'Eng. Produto', path: '/product-engineering/eng-produto' },
      { label: 'Componentes', path: '/product-engineering/componentes' },
      { label: 'Processos', path: '/product-engineering/processos' },
    ],
  },
  {
    id: 'suprimentos',
    label: 'Suprimentos',
    path: '/suprimentos',
    icon: Inventory2Outlined,
    pages: [
      { label: 'Estoque', path: '/suprimentos/estoque' },
      { label: 'Separações', path: '/suprimentos/separacoes' },
      { label: 'Movimentações', path: '/suprimentos/movimentacoes' },
      { label: 'Notas Fiscais', path: '/suprimentos/notas-fiscais' },
      { label: 'Parametrização', path: '/suprimentos/parametrizacao' },
    ],
  },
  {
    id: 'vendas',
    label: 'Vendas',
    path: '/sales',
    icon: StorefrontOutlined,
    pages: [
      { label: 'PDV', path: '/sales/pdv' },
      { label: 'Pedidos', path: '/sales/pedidos' },
      { label: 'Tabela de Vendas', path: '/sales/tabela' },
    ],
  },
  {
    id: 'sensoriamento',
    label: 'Sensoriamento',
    path: '/sensoriamento',
    icon: SensorsOutlined,
    pages: [
      { label: 'Sensores', path: '/sensoriamento/sensores' },
      { label: 'Relatórios', path: '/sensoriamento/relatorios' },
    ],
  },
  {
    id: 'cadastros',
    label: 'Cadastros',
    path: '/cadastros',
    icon: DatasetOutlined,
    pages: [
      { label: 'Produto', path: '/cadastros/produto' },
      { label: 'Itens de Consumo', path: '/cadastros/itens-consumo' },
      { label: 'Clientes', path: '/cadastros/clientes' },
      { label: 'Formas de pagamento', path: '/cadastros/formas-pagamento' },
      { label: 'Setores', path: '/cadastros/parametrizacao/setores' },
      { label: 'Máquinas', path: '/cadastros/parametrizacao/maquinas' },
      { label: 'Categorias', path: '/cadastros/parametrizacao/categorias' },
      { label: 'Causas', path: '/cadastros/parametrizacao/causas' },
    ],
  },
];

export const ADMIN_PANEL_PATH = '/admin-panel';

const byLabel = (a: { label: string }, b: { label: string }) =>
  a.label.localeCompare(b.label, 'pt-BR', { sensitivity: 'base' });

// Prioridade estimada pelo fluxo industrial, na ausência de dados de uso.
const MODULE_ORDER = ['producao', 'planejamento', 'qualidade', 'suprimentos', 'manutencao', 'vendas', 'engenharia', 'sensoriamento', 'cadastros'];

/** Aplica apenas a ordem visual; itens indisponíveis nunca são incluídos. */
export function orderNavModules<T extends { id: string }>(items: T[], order?: string[] | null): T[] {
  const remaining = new Map(items.map(item => [item.id, item]));
  const result: T[] = [];
  for (const id of order ?? []) {
    const item = remaining.get(id);
    if (item) { result.push(item); remaining.delete(id); }
  }
  return [...result, ...items.filter(item => remaining.has(item.id))];
}

/** Fixadas primeiro; dentro de cada grupo, ordem alfabética. */
export function sortPinnedFirst<T extends { label: string; path: string }>(
  items: T[],
  pinnedPaths: ReadonlySet<string>,
): T[] {
  return [...items].sort(
    (a, b) => Number(pinnedPaths.has(b.path)) - Number(pinnedPaths.has(a.path)) || byLabel(a, b),
  );
}

/**
 * Módulos por frequência estimada; páginas fixadas primeiro, em ordem alfabética
 * dentro de cada grupo.
 * Módulos sem página acessível somem.
 */
export function filterNavModules(
  hasPermission: (permission: string) => boolean,
  isSuperAdmin: boolean,
  pinnedPaths: ReadonlySet<string> = new Set(),
): NavModule[] {
  const canOpen = (path: string) => canAccessRoutePath(path, hasPermission, isSuperAdmin);
  return NAV_MODULES.filter((module) => canOpen(module.path))
    .map((module) => ({
      ...module,
      pages: sortPinnedFirst(module.pages.filter((page) => canOpen(page.path)), pinnedPaths),
    }))
    .filter((module) => module.pages.length > 0)
    .sort((a, b) => {
      const aIndex = MODULE_ORDER.indexOf(a.id);
      const bIndex = MODULE_ORDER.indexOf(b.id);
      return (aIndex < 0 ? MODULE_ORDER.length : aIndex) - (bIndex < 0 ? MODULE_ORDER.length : bIndex) || byLabel(a, b);
    });
}

/** Página ativa: rota exata ou subrota (ex.: montagem dentro de Eng. Produto). */
export function isNavPageActive(pathname: string, pagePath: string): boolean {
  return pathname === pagePath || pathname.startsWith(`${pagePath}/`);
}
